import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Alert, FormGroup, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { InputField, RHFField } from '../../components/Field';
import * as gatewayActions from '../../actions/gatewayActions';
import { GatewayApi } from '../../gatewayApi';
import { sleep } from '../../utils';
import { ColLeft, ColRight, RenderSettings } from './utils';
import { API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL } from '../../utils/constants';

// Migrated from redux-form to react-hook-form. The conditional sections that
// redux-form drove off state.form.integrationsEditForm.values now read live values
// off watch(); redux-form change() became setValue(); the `settings` FieldArray is the
// RenderSettings component. `enabled` uses register on a native checkbox; the InputFields
// that used warn={clearMessage} now clear the message via RHFField onValueChange.
function IntegrationsEditForm({ currentStore, currentGatewayStore, storeIntegration, initialValues }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const availableInterfaces = useSelector((s) => s.gateway.availableInterfaces);
  const azureOperations = useSelector((s) => s.gateway.azureOperations);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);

  const { control, register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: initialValues || {},
  });

  const initKey = JSON.stringify(initialValues);
  useEffect(() => {
    if (initialValues) reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  useEffect(() => {
    dispatch(gatewayActions.getAvailableInterfaces());
    dispatch(gatewayActions.getAzureOperations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearMessage = () => {
    setMessage('');
    setError(false);
  };

  const submit = async (store) => {
    try {
      const settings = {};
      if (store.settings) {
        store.settings.forEach((item) => {
          settings[item.name] = item.value;
        });
      }

      let integration = storeIntegration ? storeIntegration.integration : store.integration;
      const [provider, product, version, interfaceName] = store.interface.split('-');
      const { accessOperation, refreshOperation, ...otherConnection } = store.connection;
      const data = {
        storeId: currentGatewayStore.storeId,
        integration: store.integration,
        enabled: store.enabled,
        interface: { provider, product, version, interface: interfaceName },
        connection: otherConnection,
        settings,
      };

      if (storeIntegration) {
        if (accessOperation) {
          const [provider, product, version, objInterface, operation] = accessOperation.split('-');
          data.connection.accessOperation = {
            provider,
            product,
            version,
            interface: objInterface,
            operation,
          };
        }
        if (refreshOperation) {
          const [provider, product, version, objInterface, operation] = refreshOperation.split('-');
          data.connection.refreshOperation = {
            provider,
            product,
            version,
            interface: objInterface,
            operation,
          };
        }
      }

      if (!storeIntegration) {
        // check when creating a integration
        const res = await GatewayApi.findStoreIntegration(currentGatewayStore.storeId, integration);
        if (res) {
          throw new Error(`Integration "${integration}" has already existed in the store.`);
        }
      }
      await GatewayApi.saveStoreIntegration(currentGatewayStore.storeId, integration, data);
      setMessage('Saved successfully');
      setError(false);
      if (!storeIntegration) {
        await sleep(2000);
        navigate(`/stores/${currentStore.id}/gateway/integrations/${integration}`);
        dispatch({ type: 'GATEWAY_STORE_INTEGRATION_GETONE_INIT' });
        dispatch(gatewayActions.getStoreIntegration(currentGatewayStore.storeId, integration));
      }
    } catch (err) {
      console.error('error:', err);
      setMessage('Failed to save, reason: ' + err.message);
      setError(true);
    }
    return false;
  };

  const onCancel = () => {
    navigate(`/stores/${currentStore.id}/gateway/settings`);
  };

  const onDelete = async () => {
    if (!window.confirm('Are you sure to delete this item?')) {
      return;
    }

    try {
      await GatewayApi.deleteStoreIntegration(currentGatewayStore.storeId, storeIntegration.integration);
      setMessage('Deleted successfully');
      setError(false);
      await sleep(1000);
      navigate(`/stores/${currentStore.id}/gateway/integrations`);
      dispatch(gatewayActions.getStore(currentStore.storeId));
    } catch (err) {
      console.error('error:', err);
      setMessage('Failed to delete, reason: ' + err.message);
      setError(true);
    }
  };

  const handleInterfaceChange = (newValue) => {
    const value = newValue.split('-');
    const [provider, product, version, interfaceName] = value;
    let integration = product;
    if (product === 'Azure') {
      integration = 'ActiveDirectory';
    } else if (product === 'X3') {
      integration = 'SageX3';
    }
    if (product === API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL) {
      setValue('connection.authType', 'BEARER');
    }
    setValue('integration', integration);
  };

  if (!availableInterfaces || !azureOperations || !initialValues) {
    return <>Loading...</>;
  }

  const values = watch();
  const operations = [],
    operationOptions = [];
  let isAuthenticationReadOnly = false;

  if (storeIntegration) {
    if (storeIntegration.integration === API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL) {
      isAuthenticationReadOnly = true;

      azureOperations.forEach((item) => {
        operationOptions.push(item);
      });
    }

    if (storeIntegration.operations) {
      Object.keys(storeIntegration.operations).forEach((name) => {
        operations.push(name);
        operationOptions.push({
          title: values.interface.replaceAll('-', ' ') + ' ' + name,
          value: values.interface + '-' + name,
        });
      });
    }
  }

  if (values && values.integration === API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL) {
    isAuthenticationReadOnly = true;
  }

  const interfaceReg = register('interface', { required: true });
  const accessOpReg = register('connection.accessOperation');
  const refreshOpReg = register('connection.refreshOperation');

  return (
    <>
      <form onSubmit={handleSubmit(submit)} className="form-horizontal">
        {message && <Alert color={error ? 'danger' : 'success'}>{message}</Alert>}
        <FormGroup row>
          <ColLeft>Interface</ColLeft>
          <ColRight>
            {storeIntegration && (
              <input className="form-control" value={values.interface.replaceAll('-', ' ')} disabled readOnly />
            )}
            {!storeIntegration && (
              <select
                {...interfaceReg}
                onChange={(e) => {
                  interfaceReg.onChange(e);
                  handleInterfaceChange(e.target.value);
                }}
                required
                className="form-control"
                id="interface"
              >
                <option key={-1} />
                {availableInterfaces.map((availableInterface, i) => (
                  <option key={i} value={availableInterface.value}>
                    {availableInterface.title}
                  </option>
                ))}
              </select>
            )}
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Integration</ColLeft>
          <ColRight>
            <RHFField name="integration" control={control} component={InputField} id="integration" readOnly required />
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Enabled</ColLeft>
          <ColRight>
            <input type="checkbox" id="enabled" className="form-check-input" {...register('enabled')} />
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Connection</ColLeft>
          <ColRight>
            <RHFField
              name="connection.uri"
              control={control}
              component={InputField}
              id="connection.uri"
              onValueChange={clearMessage}
            />
          </ColRight>
        </FormGroup>
        {values.connection.uri.toLowerCase().startsWith('https') && (
          <>
            <FormGroup row>
              <ColLeft>&bull; SSL auth</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.clientCertificate"
                  control={control}
                  component={InputField}
                  id="connection.clientCertificate"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
            <FormGroup row>
              <ColLeft>&bull; SSL trust</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.trustCertificate"
                  control={control}
                  component={InputField}
                  id="connection.trustCertificate"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
          </>
        )}
        <FormGroup row>
          <ColLeft>Authentication</ColLeft>
          <ColRight>
            <select
              {...register('connection.authType')}
              required
              className="form-control"
              id="connection.authType"
              readOnly={isAuthenticationReadOnly}
            >
              {!isAuthenticationReadOnly && (
                <>
                  <option value="NONE">NONE</option>
                  <option value="BASIC">BASIC</option>
                </>
              )}
              <option value="BEARER">OAUTH2</option>
            </select>
          </ColRight>
        </FormGroup>
        {values.connection.authType === 'BASIC' && (
          <>
            <FormGroup row>
              <ColLeft>&bull; username</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.username"
                  control={control}
                  component={InputField}
                  id="connection.username"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
            <FormGroup row>
              <ColLeft>&bull; password</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.password"
                  control={control}
                  component={InputField}
                  id="connection.password"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
          </>
        )}
        {values.connection.authType === 'BEARER' && (
          <>
            <FormGroup row>
              <ColLeft>&bull; access operation</ColLeft>
              <ColRight>
                {!storeIntegration && (
                  <input value="Please set this field after the ingration is created." className="form-control" readOnly />
                )}
                {storeIntegration && (
                  <select
                    {...accessOpReg}
                    onChange={(e) => {
                      accessOpReg.onChange(e);
                      clearMessage();
                    }}
                    className="form-control"
                    id="connection.accessOperation"
                  >
                    <option value=""></option>
                    {operationOptions.map((operation, i) => (
                      <option key={i} value={operation.value}>
                        {operation.title}
                      </option>
                    ))}
                  </select>
                )}
              </ColRight>
            </FormGroup>
            <FormGroup row>
              <ColLeft>&bull; access token</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.accessToken"
                  control={control}
                  component={InputField}
                  id="connection.accessToken"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
            <FormGroup row>
              <ColLeft>&bull; refresh operation</ColLeft>
              <ColRight>
                {!storeIntegration && (
                  <input value="Please set this field after the ingration is created." className="form-control" readOnly />
                )}
                {storeIntegration && (
                  <select
                    {...refreshOpReg}
                    onChange={(e) => {
                      refreshOpReg.onChange(e);
                      clearMessage();
                    }}
                    className="form-control"
                    id="connection.refreshOperation"
                  >
                    <option value=""></option>
                    {operationOptions.map((operation, i) => (
                      <option key={i} value={operation.value}>
                        {operation.title}
                      </option>
                    ))}
                  </select>
                )}
              </ColRight>
            </FormGroup>
            <FormGroup row>
              <ColLeft>&bull; refresh token</ColLeft>
              <ColRight>
                <RHFField
                  name="connection.refreshToken"
                  control={control}
                  component={InputField}
                  id="connection.refreshToken"
                  onValueChange={clearMessage}
                />
              </ColRight>
            </FormGroup>
          </>
        )}
        <FormGroup row style={{ marginBottom: 0 }}>
          <ColLeft>Settings</ColLeft>
          <ColRight>
            <RenderSettings control={control} register={register} name="settings" />
          </ColRight>
        </FormGroup>
        {storeIntegration && (
          <div className="float-start">
            <Button type="button" color="danger" className="me-2" onClick={() => onDelete()}>
              Delete
            </Button>
          </div>
        )}
        <div className="float-end">
          <Button type="button" color="secondary" className="me-2" onClick={() => onCancel()}>
            Cancel
          </Button>
          <Button type="submit" color="primary">
            Save
          </Button>
        </div>
        <div style={{ clear: 'both' }}></div>
        <div style={{ marginTop: 5 }}>{message && <Alert color={error ? 'danger' : 'success'}>{message}</Alert>}</div>
      </form>
      {storeIntegration && (
        <>
          <div className="row">
            <div className="col-sm-12 mt-2">
              <hr size={1} style={{ width: '100%' }} />
            </div>
            <div className="col-sm-3 col-lg-2 col-xl-2">Operations</div>
            <div className="col-sm-6 col-lg-4 col-xl-2">
              <ul className="list-unstyled">
                {operations.map((operation, i) => (
                  <li key={i}>
                    <Link
                      style={{ color: 'inherit', textDecoration: 'inherit' }}
                      to={`/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}/${operation}`}
                    >
                      <i className="icon-pencil" style={{ cursor: 'pointer' }} title="Edit Item" />
                    </Link>
                    &nbsp;&nbsp;
                    <Link
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                      to={`/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}/${operation}`}
                    >
                      {operation}
                    </Link>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                color="primary"
                onClick={() => {
                  navigate(
                    `/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}/operations-add`
                  );
                }}
              >
                Add Operation
              </Button>
            </div>
            <div className="col-sm-3 col-lg-4 col-xl-2">
              <div className="float-end">
                <Button
                  type="button"
                  color="primary"
                  onClick={() => {
                    navigate(
                      `/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}/operations-add`
                    );
                  }}
                >
                  Add Operation
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default IntegrationsEditForm;
